import auth from './PluginAuth.ts'

class LazyLoad {
  private options: LazyOptions = {
    rootMargin: '0px',
    root: null,
    threshold: 0,
    imageOptions: {
      classDefault: '',
      classOnLoad: '',
    },
    mode: LazyMode.EXCLUDE,
    url: '',
  }

  constructor(options: LazyOptions) {
    // Default options
    this.options = options

    this.setUpObserver()
  }

  setUpObserver() {
    if ('IntersectionObserver' in window) {
      const lazyImagesObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              new LazyImage(
                entry.target as HTMLImageElement,
                this.options.url,
                this.options.imageOptions,
              )

              observer.unobserve(entry.target)
            }
          })
        },
        this.getObserverOptions(),
      )

      this.observeTargets(lazyImagesObserver)
    }
  }

  observeTargets(observer: IntersectionObserver): void {
    this.getTargets().forEach((lazyImage) => {
      if (lazyImage.getAttribute('data-lazy-already-init') !== 'true') {
        lazyImage.setAttribute('data-lazy-already-init', 'true')

        lazyImage.classList.add(this.options.imageOptions.classDefault)
        observer.observe(lazyImage)
      }
    })
  }

  getObserverOptions(): IntersectionObserverInit {
    const observerOptions: IntersectionObserverInit = {
      root: this.options.root,
      rootMargin: this.options.rootMargin,
      threshold: this.options.threshold,
    }

    return observerOptions
  }

  getTargets(): NodeListOf<Element> {
    return document.querySelectorAll(this.getSelector())
  }

  getSelector(): string {
    if (this.options.mode === LazyMode.EXCLUDE) {
      return this.getExcludeSelector()
    } else {
      return this.getIcludeSelector()
    }
  }

  getExcludeSelector(): string {
    if (this.options.selectors) {
      return `img:not(${this.options.selectors.map((sel) => sel).join(', ')})`
    } else {
      return 'img'
    }
  }

  getIcludeSelector(): string {
    if (this.options.selectors) {
      return `${this.options.selectors.map((sel) => `img${sel}`).join(', ')}`
    } else {
      return 'img'
    }
  }
}

class LazyImage {
  img: HTMLImageElement
  options: LazyImageOptions = {
    classDefault: 'lazy-image',
    classOnLoad: 'lazy-image--active',
  }
  url: string
  generationMode: GenerationMode = GenerationMode.MATCHED

  constructor(img: HTMLImageElement, url: string, options?: LazyImageOptions) {
    this.img = img
    this.url = url

    if (options) {
      this.options = options
    }

    this.getGenerationMode()
    this.init()
  }

  init() {
    if (!this.isLoaded()) {
      this.img.setAttribute('data-lazy-loading', 'true')
      this.fetchImage()
    }
  }

  async fetchImage() {
    const size = Math.round(this.calculateSize())

    try {
      const token = await auth.getToken()
      const response = await fetch(
        `${this.url}?id=${this.getId()}&width=${size}&height=${size}&type=${
          this.generationMode
        }&token=${token}`,
      )

      if (!response.ok) return

      /*       const contentType = response.headers.get('content-type')

      if (!contentType) return */

      const result = await response.json()
      const res = result as ImageRespone

      if (res.items.length > 0) {
        this.img.addEventListener('load', (e) => this.onLoad(e))
        this.changeSource(res.items[0].thumbnail.cached.path)
      }
    } catch (error) {
      console.warn('[Lazy Loading] Error fetching image from URL')
    }
  }

  calculateSize(): number {
    const width = this.img.offsetWidth
    const height = this.img.offsetHeight

    return width > height ? width : height
  }

  getId(): string {
    const src = this.img.src
    const splited = src.split('?')

    if (splited.length > 1) {
      return splited[1].replace('v=', '')
    } else {
      return 'null'
    }
  }

  getGenerationMode(): void {
    if (this.img.hasAttribute('data-gen')) {
      const mode = this.img.getAttribute('data-gen')

      if (mode) {
        this.generationMode =
          mode === GenerationMode.CROP
            ? GenerationMode.CROP
            : GenerationMode.MATCHED
      }
    }
  }

  changeSource(source: string): void {
    this.img.src = source
  }

  isLoaded(): boolean {
    return (
      this.img.getAttribute('data-lazy-loaded') == 'true' ||
      this.img.getAttribute('data-lazy-loading') == 'true'
    )
  }

  onLoad(e: Event): void {
    const target = e.target as HTMLElement
    target.classList.add(this.options.classOnLoad)

    target.removeAttribute('data-lazy-loading')
    target.setAttribute('data-lazy-loaded', 'true')
  }
}

export type LazyOptions = {
  rootMargin: string
  root: Element | null
  threshold: number
  mode: LazyMode
  imageOptions: LazyImageOptions
  selectors?: string[]
  url: string
}

export type LazyImageOptions = {
  classDefault: string
  classOnLoad: string
}

type ImageRespone = {
  items: {
    name: string
    filename: string
    created_ad: string
    thumbnail: {
      raw: {
        path: string
        height: number
        width: number
        size: number
        'mime-type': string
      }
      cached: {
        path: string
        height: number
        width: number
        size: number
        'mime-type': string
      }
    }
  }[]
}

export enum LazyMode {
  INCLUDE,
  EXCLUDE,
}

export enum GenerationMode {
  CROP = 'crop',
  MATCHED = 'matched',
}

export default LazyLoad
